/**
 * Which arrival covers a person has already seen.
 *
 * An arrival is worth seeing once. After that it is a toll gate, so we remember
 * it and walk straight into the page. Kept in localStorage per user so it is
 * instant with no flash, and synced to the server so a second device agrees.
 */
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY = (uid) => `fp_arrivals_seen_${uid || 'anon'}`;

let currentUid = null;
let cache = new Set();

const persist = () => {
  try { localStorage.setItem(KEY(currentUid), JSON.stringify([...cache])); } catch { /* private mode */ }
};

export const loadArrivals = (uid) => {
  currentUid = uid;
  try {
    cache = new Set(JSON.parse(localStorage.getItem(KEY(uid)) || '[]'));
  } catch { cache = new Set(); }
  return cache;
};

/** Merge whatever the server remembers — covers a new browser or device. */
export const syncArrivals = async (uid, token) => {
  if (!uid || !token) return;
  loadArrivals(uid);
  try {
    const r = await axios.get(`${API}/arrivals`, { headers: { Authorization: `Bearer ${token}` } });
    (r.data?.seen || []).forEach((k) => cache.add(k));
    persist();
  } catch { /* local memory still works */ }
};

export const hasSeenArrival = (key) => cache.has(key);

export const markArrivalSeen = (key, token) => {
  if (!key || cache.has(key)) return;
  cache.add(key);
  persist();
  if (token) {
    axios.post(`${API}/arrivals/${encodeURIComponent(key)}`, {},
      { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
  }
};

export const forgetArrival = async (key, token) => {
  cache.delete(key);
  persist();
  if (token) {
    try {
      await axios.delete(`${API}/arrivals/${encodeURIComponent(key)}`,
        { headers: { Authorization: `Bearer ${token}` } });
    } catch { /* local forget still holds until the next sync */ }
  }
};

export const clearArrivals = () => { cache = new Set(); currentUid = null; };

/** Remember which arrival belongs to which route, so the layout can offer a replay. */
const MAP_KEY = 'fp_arrival_routes';

export const registerArrivalKey = (path, key) => {
  if (!path || !key) return;
  try {
    const m = JSON.parse(localStorage.getItem(MAP_KEY) || '{}');
    if (m[path] === key) return;
    m[path] = key;
    localStorage.setItem(MAP_KEY, JSON.stringify(m));
  } catch { /* private mode */ }
};

export const arrivalKeyForPath = (path) => {
  try { return (JSON.parse(localStorage.getItem(MAP_KEY) || '{}'))[path] || null; }
  catch { return null; }
};
